import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Grid, Button, Chip, Divider } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import { TitleScreen, SubTitleScreen } from '../../SystemDesign/Styles';
import SearchInput from '../../SystemDesign/SearchInput';
import LoaderSpinner from '../../SystemDesign/LoaderSpinner';
import AddIcon from '@mui/icons-material/Add';
import AddEvaluation from './AddEvaluation';
import ViewToggle from '../../SystemDesign/ViewToggle';
import QuizEvaluationListAdmin from '../Quiz_Evaluation/QuizEvaluationListAdmin';
import PracticalEvidenceList from './PracticalEvidences/PracticalEvidenceList';
import { useGetAllEvaluationsQuery } from '../../services/SkillsManagement';
import CardMenuActions from '../../SystemDesign/CardMenuActions';
import DeleteEvaluation from './DeleteEvaluation';
import SeeMoreEvaluation from './SeeMoreEvaluation';
import UpdateEvaluation from './UpdateEvaluation';
import PaginationUI from '../../SystemDesign/PaginationUI';
import { INITIALIZE_EVALUATIONS } from '../../Reducers/Actions';

const EvaluationList = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = tokens(theme.palette.mode);
  const [openAdd, setOpenAdd] = useState(false);
  const [viewMode, setViewMode] = useState('cards');
  const [oralSearch, setOralSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Use the new evaluations query with standard typing
  const evaluationsQueryArgs = useMemo(() => ({
    filters: {},
    pagination: { currentPage: 1, rowsPerPage: 1000 }, // Get all evaluations
    sorting: { column: 'evaluation_date', direction: 'desc' },
  }), []);

  const { 
    data: evaluationsResp, 
    isLoading: isLoadingEvaluations, 
    isFetching: isFetchingEvaluations 
  } = useGetAllEvaluationsQuery(evaluationsQueryArgs, { 
    refetchOnMountOrArgChange: false 
  });

  // Extract evaluations from API response
  const evaluationsFromApi = useMemo(() => {
    return evaluationsResp?.evaluations || [];
  }, [evaluationsResp]);

  // Read evaluations from Redux store
  const evalSlice = useSelector((state) => state?.evaluations);
  const evalFromStoreRaw = Array.isArray(evalSlice) ? evalSlice : (evalSlice?.items || []);
  const deletedEvalIds = useMemo(() => {
    const arr = Array.isArray(evalSlice?.deletedIds) ? evalSlice.deletedIds : [];
    return new Set(arr.map((x) => String(x)));
  }, [evalSlice]);

  // Initialize evaluations reducer from API data
  useEffect(() => {
    const storeCount = Array.isArray(evalFromStoreRaw) ? evalFromStoreRaw.length : 0;
    if (storeCount === 0 && evaluationsFromApi.length > 0) {
      dispatch({ type: INITIALIZE_EVALUATIONS, payload: evaluationsFromApi });
    }
  }, [evaluationsFromApi, dispatch, evalFromStoreRaw]);

  // Combine API and Redux evaluations
  const evaluations = useMemo(() => {
    const byId = new Map();

    // 1) Seed from Redux store (authoritative)
    for (const ev of evalFromStoreRaw) {
      const id = ev?.evaluation_id ?? ev?.id;
      if (id == null) continue;
      const key = String(id);
      byId.set(key, {
        ...ev,
        evaluation_id: ev.evaluation_id || ev.id,
        employee_id: ev.employee_id,
        skill_id: ev.skill_id,
        evaluation_date: ev.evaluation_date,
        score: ev.score,
        level: ev.level,
        comments: ev.comments,
        evaluation_type: ev.evaluation_type,
        rater_type_id: ev.rater_type_id,
        evaluatedBy: ev.evaluatedBy,
        createdAt: ev.createdAt,
        updatedAt: ev.updatedAt,
        evidences: Array.isArray(ev?.evidences) ? ev.evidences : [],
      });
    }

    // 2) Add from API only if missing from Redux and not deleted
    for (const apiEv of evaluationsFromApi) {
      const key = String(apiEv.evaluation_id);
      if (byId.has(key)) continue;
      if (deletedEvalIds.has(key)) continue;
      byId.set(key, {
        ...apiEv,
        evidences: Array.isArray(apiEv?.evidences) ? apiEv.evidences : [],
      });
    }

    return Array.from(byId.values());
  }, [evaluationsFromApi, evalFromStoreRaw, deletedEvalIds]);

  // Group evaluations by employee
  const sessions = useMemo(() => {
    const map = new Map();
    
    for (const ev of evaluations) {
      const empId = ev.employee_id;
      const key = String(empId);
      const employeeName = ev.employee?.username || ev.employeeName || `Employee #${empId}`;
      const evaluatorUser = ev.evaluatedBy;
      const evaluatorId = evaluatorUser?.id;
      const evaluatorName = evaluatorUser?.username || evaluatorUser?.email || ev.evaluatorName || '-';
      const evalDate = ev.evaluation_date ? new Date(ev.evaluation_date) : undefined;

      if (!map.has(key)) {
        map.set(key, {
          id: key,
          employeeName,
          evaluatorName,
          evaluatedAt: evalDate?.toISOString(),
          posteName: ev.employee.Poste?.name || ev.posteName || '-',
          posteId: ev.employee.Poste?.poste_id || ev.posteId,
          posteSkillRequirements: ev.skill?.skillRequirements || [],
          evaluationType: ev.evaluation_type,
          skills: [],
          _evaluatorIdSet: new Set(evaluatorId ? [evaluatorId] : []),
          _lastEvaluatorName: evaluatorName,
        });
      }
      
      const entry = map.get(key);

      // Aggregate evaluator info
      if (evaluatorId) {
        entry._evaluatorIdSet.add(evaluatorId);
        entry._lastEvaluatorName = evaluatorName || entry._lastEvaluatorName;
      }
      entry.evaluatorName = entry._evaluatorIdSet.size > 1 ? 'Multiple' : (entry._lastEvaluatorName || '-');

      // Keep latest date
      if (evalDate) {
        const prev = entry.evaluatedAt ? new Date(entry.evaluatedAt) : undefined;
        if (!prev || evalDate > prev) entry.evaluatedAt = evalDate.toISOString();
      }

      // Push skill data
      entry.skills.push({
        evaluationId: ev.evaluation_id,
        skillId: ev.skill_id,
        skillName: ev.skill?.name || ev.skillName || `Skill #${ev.skill_id}`,
        skillCode: ev.skill?.skill_code || ev.skillCode,
        discipline: ev.skill?.discipline || ev.discipline,
        comments: ev.comments,
        score: ev.score,
        level: ev.level,
        evaluationType: ev.evaluation_type,
        evaluation_date: ev.evaluation_date,
        evidences: ev.evidences,
      });
    }

    // Clean helper fields
    return Array.from(map.values()).map(({ _evaluatorIdSet, _lastEvaluatorName, ...rest }) => rest);
  }, [evaluations]);

  // Filter sessions based on search
  const filteredSessions = useMemo(() => {
    const term = (oralSearch || '').toLowerCase().trim();
    if (!term) return sessions;
    return sessions.filter((s) => {
      const hay = [
        s.employeeName,
        s.evaluatorName,
        s.posteName,
        ...(s.skills?.flatMap((sk) => [sk.skillName, sk.comments]) || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(term);
    });
  }, [sessions, oralSearch]);

  // Pagination logic
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((filteredSessions?.length || 0) / rowsPerPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [filteredSessions, rowsPerPage]);

  useEffect(() => { 
    setCurrentPage(1); 
  }, [oralSearch, viewMode]);

  const paginationInfo = useMemo(() => ({
    currentPage,
    rowsPerPage,
    total: filteredSessions?.length || 0,
  }), [currentPage, rowsPerPage, filteredSessions]);

  const paginatedSessions = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredSessions.slice(start, end);
  }, [filteredSessions, currentPage, rowsPerPage]);

  const [expandedId, setExpandedId] = useState(null);
  
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('evaluationList.expandedId');
      if (saved) setExpandedId(saved);
    } catch (_) {}
  }, []);

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography sx={TitleScreen(colors)}>
            Evaluations 
          </Typography>
          <Typography sx={SubTitleScreen(colors)}>
            Manage evaluations 
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenAdd(true)}
          >
            Add Evaluation
          </Button>
        </Box>
      </Box>

      {viewMode !== 'table' && (
        <Box p={3} sx={{ mb: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ flexGrow: 1 }}>
              <SearchInput
                placeholder="Search evaluations..."
                value={oralSearch}
                onChange={setOralSearch}
                onSearch={setOralSearch}
                debounce={300}
              />
            </div>
            <ViewToggle
              value={viewMode}
              onChange={(next) => setViewMode(next)}
              cardsLabel="Oral"
              tableLabel="Written"
              label="Evaluation type:"
            />
          </div>
        </Box>
      )}
      
      {(isLoadingEvaluations) && (
        <LoaderSpinner loading={true} />
      )}

      {viewMode === 'table' ? (
        <QuizEvaluationListAdmin viewMode={viewMode} onChangeViewMode={setViewMode} />
      ) : (
        <Box p={3}>
          {!isLoadingEvaluations && sessions.length === 0 && (
            <Box sx={{ p: 3, borderRadius: 2, border: `1px dashed ${colors.grey[300]}` }}>
              <Typography color={colors.grey[600]}>No evaluations yet.</Typography>
            </Box>
          )}

          {paginatedSessions.map((session) => (
            <Accordion
              key={session.id}
              sx={{ mb: 2 }}
              expanded={String(expandedId) === String(session.id)}
              onChange={(_, isExpanded) => {
                const next = isExpanded ? String(session.id) : null;
                setExpandedId(next);
                try {
                  if (next) sessionStorage.setItem('evaluationList.expandedId', String(next));
                  else sessionStorage.removeItem('evaluationList.expandedId');
                } catch (_) {}
              }}
              TransitionProps={{ timeout: 0, mountOnEnter: true, unmountOnExit: false }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ width: '100%' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                      {session.employeeName}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Position: {session.posteName || '-'} • Evaluated by: {session.evaluatorName || '-'} • Last evaluation: {session.evaluatedAt ? new Date(session.evaluatedAt).toLocaleDateString() : '-'} • Evaluated on: {session.skills?.length ?? 0} skill(s)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Divider sx={{ mb: 2 }} />
                <EvaluationSessionDetails
                  colors={colors}
                  session={session}
                  onUpdated={() => {
                    const sid = String(session.id);
                    setExpandedId(sid);
                    try { sessionStorage.setItem('evaluationList.expandedId', sid); } catch (_) {}
                    setTimeout(() => {
                      setExpandedId(sid);
                      try { sessionStorage.setItem('evaluationList.expandedId', sid); } catch (_) {}
                    }, 0);
                  }}
                />
              </AccordionDetails>
            </Accordion>
          ))}

          {filteredSessions.length > 0 && (
            <PaginationUI
              paginationInfo={paginationInfo}
              isLoading={isLoadingEvaluations || isFetchingEvaluations}
              onPageChange={(page) => setCurrentPage(page)}
              onRowsPerPageChange={(rows) => { setRowsPerPage(rows); setCurrentPage(1); }}
            />
          )}
        </Box>
      )}
      
      <AddEvaluation
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        allowedEvaluationTypes={['oral']}
      />
    </Box>
  );
};

// EvaluationSessionDetails component remains the same as in your original code
function EvaluationSessionDetails({ colors, session, onUpdated }) {
  const reqMap = useMemo(() => {
    const requirements = Array.isArray(session?.posteSkillRequirements) ? session.posteSkillRequirements : [];
    const m = new Map();
    for (const r of requirements) {
      const sid = r?.skill_id ?? r?.skill?.skill_id ?? r?.skill?.id;
      if (sid != null) m.set(Number(sid), r);
    }
    return m;
  }, [session?.posteSkillRequirements]);

  const levelText = (value) => {
    if (value == null || value === '') return '-';
    const raw = typeof value === 'string' ? value.trim().toLowerCase() : value;
    const num = Number(raw);
    if (Number.isFinite(num)) {
      if (num === 1) return 'Awareness (1)';
      if (num === 2) return 'Skilled Application (2)';
      if (num === 3) return 'Management (3)';
      return String(num);
    }
    if (raw.includes('aware')) return 'Awareness';
    if (raw.includes('skilled')) return 'Skilled Application';
    if (raw.includes('manage')) return 'Management';
    return String(value);
  };

  return (
    <Grid container spacing={2}>
      {(session.skills || []).map((skill, idx) => {
        const req = reqMap.get(Number(skill.skillId));
        const minScore = Number.isFinite(Number(req?.min_score)) ? Number(req?.min_score) : undefined;
        const maxScore = Number.isFinite(Number(req?.max_score)) ? Number(req?.max_score) : undefined;
        const requiredLevel = req?.required_level ?? '-';
        const requiredRangeText = (minScore != null && maxScore != null)
          ? `${minScore}-${maxScore}/10`
          : '-';
        const reqSkillName = req?.skill?.name || req?.skill?.skill_name;
        const displaySkillName = skill.skillName && !/^Skill #/.test(String(skill.skillName))
          ? skill.skillName
          : (reqSkillName || (skill.skillId != null ? `Skill #${skill.skillId}` : 'Skill'));

        const obtainedLevelRaw = skill.level;

        return (
          <Grid item size={{ xs: 12 }} key={idx}>
            <Box sx={{ p: 2, borderRadius: 1, border: `1px solid ${colors.grey[200]}` }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle1" fontWeight={600}>{displaySkillName}</Typography>
                  {skill.skillCode && <Chip size="small" variant="outlined" label={skill.skillCode} />}
                  {skill.discipline && <Chip size="small" variant="outlined" label={skill.discipline} />}
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {skill.evaluationType && (
                    <Chip size="small" color="primary" variant="outlined" label={String(skill.evaluationType)} />
                  )}
                  <CardMenuActions>
                    <SeeMoreEvaluation
                      row={{
                        original: {
                          evaluationId: skill.evaluationId,
                          id: skill.evaluationId,
                          evaluation_id: skill.evaluationId,
                          score: skill.score,
                          comments: skill.comments,
                          level: skill.level,
                          evaluation_type: skill.evaluationType,
                          evaluation_date: skill.evaluation_date,
                          skill_id: skill.skillId,
                          skillName: skill.skillName,
                          skillCode: skill.skillCode,
                          discipline: skill.discipline,
                          evidences: skill.evidences || [],
                          employeeName: session?.employeeName,
                          posteName: session?.posteName,
                          required_min_score: minScore,
                          required_max_score: maxScore,
                          required_level: requiredLevel,
                        },
                      }}
                    />
                    <UpdateEvaluation
                      row={{
                        original: {
                          evaluationId: skill.evaluationId,
                          id: skill.evaluationId,
                          evaluation_id: skill.evaluationId,
                          score: skill.score,
                          comments: skill.comments,
                          level: skill.level,
                          evaluation_type: skill.evaluationType,
                          skill_id: skill.skillId,
                          evaluation_date: skill.evaluation_date,
                          title: skill.skillName,
                        },
                      }}
                      onUpdated={onUpdated}
                    />
                    <DeleteEvaluation 
                      row={{ original: { 
                        evaluationId: skill.evaluationId, 
                        id: skill.evaluationId,
                        evaluation_id: skill.evaluationId,
                        title: skill.skillName 
                      } }} 
                    />
                  </CardMenuActions>
                </Box>
              </Box>
              {skill.comments && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
                  "{skill.comments}"
                </Typography>
              )}
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Required Score</Typography>
                  <Typography variant="body1" fontWeight={600}>{requiredRangeText}</Typography>
                </Grid>
                <Grid item size={{ xs: 12, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Obtained Score</Typography>
                  <Typography variant="body1" fontWeight={700}>{(skill.score ?? '-') + '/10'}</Typography>
                </Grid>
                <Grid item size={{ xs: 12, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Required Level</Typography>
                  <Typography variant="body1">{requiredLevel}</Typography>
                </Grid>
                <Grid item size={{ xs: 12, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Obtained Level</Typography>
                  <Typography variant="body1">{levelText(obtainedLevelRaw)}</Typography>
                </Grid>
              </Grid>
              <Box sx={{ mt: 1 }}>
                <PracticalEvidenceList evaluationId={skill.evaluationId} evidences={skill.evidences} />
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}

export default EvaluationList;